import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError, api } from '../../api/client';
import type { EventSlots } from '../../api/types';
import { Banner, Card, formatDate, secondaryLabel } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import EventSlotsEditor from './EventSlotsEditor';

/**
 * Administration d'une seule date.
 *
 * Même contenu que la vue de période, réduit à un événement — c'est ce qu'on
 * ouvre le jeudi soir quand une personne se décommande pour le dimanche. La
 * date est dans l'URL, donc la page se partage et se met en favori.
 */
export default function EventAdminScreen() {
  const params = useParams();
  const runId = Number(params.runId);
  const eventId = Number(params.eventId);

  const slots = useAsync<EventSlots>(() => api.eventSlots(runId, eventId), [runId, eventId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const change = (roleId: number, person: number | null, previous: number | null) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    api
      .updateSlot(runId, eventId, roleId, {
        person_id: person,
        previous_person_id: previous,
        lock: true,
      })
      .then((updated) => {
        slots.setData(updated);
        setNotice(
          person === null
            ? 'Poste libéré. Le choix est enregistré.'
            : 'Affectation enregistrée et figée : une régénération la conservera.',
        );
      })
      .catch((cause) => {
        setError(cause instanceof ApiError ? cause.detail : 'Action impossible.');
      })
      .finally(() => setBusy(false));
  };

  if (!Number.isFinite(runId) || !Number.isFinite(eventId)) {
    return <Banner kind="error">Adresse incomplète.</Banner>;
  }

  const data = slots.data;
  const secondary = data ? secondaryLabel(data.event_label, data.starts_at) : null;

  return (
    <div className="stack">
      <Link className="ghost small" to="/affectations">
        ← Toute la période
      </Link>

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="success">{notice}</Banner>}
      {slots.error && <Banner kind="error">{slots.error}</Banner>}

      {slots.loading && <p className="muted">Chargement…</p>}

      {data && (
        <Card title={formatDate(data.starts_at)}>
          {secondary && <p className="muted">{secondary}</p>}
          {!data.editable && (
            <Banner kind="info">
              Ce planning n’est plus modifiable. Reprenez-le en brouillon pour y
              travailler.
            </Banner>
          )}
          <EventSlotsEditor slots={data} busy={busy} onChange={change} />
        </Card>
      )}
    </div>
  );
}
